import { useNotify, useRedirect, useRefresh, Title, useGetIdentity } from 'react-admin';
import { useParams } from 'react-router-dom';
import { adsProxy, aiProxy, aiSummariesProxy, customReasonsProxy, type ExpertModerationReturn } from './apiGateway';
import { Box, Typography, Card, CardContent, Button, Stack, Skeleton, Alert, Modal, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, TextField, CircularProgress, FormControl, InputLabel, Select, MenuItem, Switch, Tooltip, Link as MuiLink, Chip, Checkbox, LinearProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useState, useEffect, useRef, useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { DEFAULT_ADS_REASONS, ReasonGroup, ReasonType } from './reasonsConfig';
import { useAdScreenLock } from './useAdScreenLock';
import { canOpenPlatformUserShow, getPlatformUserShowUrl } from './platformUserShowLink';
import { AiModerationResultCard, type AiModeration } from './AiModerationResultCard';

interface AdInfo {
    gender: string;
    category: string;
    subcategory: string;
    attachmentPhotoIds: string[];
    clothStatus: string;
    brand: string;
    model: string;
    size: string;
    country: string;
    fullPrice: number;
    city: string;
    isExchange: boolean;
    isVintage: boolean;
    isExistBox: boolean;
    description: string;
    serialNumber?: string;
}

interface UserInfo {
    id: string;
    name: string;
    rating: number;
    reviewsCount: number;
    countActiveAds: number;
    isActive: boolean;
    avatarUrl?: string;
}

interface ModerationAdDetail {
    id: string;
    info: AdInfo;
    currentStatus: string;
    statusHistory: string[];
    creationDate: string;
    lastUpdateDate: string;
    userInfo: UserInfo;
    aiModeration?: AiModeration | null;
}

interface ApiResponse {
    success: boolean;
    body: ModerationAdDetail;
}

interface AdConfigResponse {
    categoriesByGender: CategoryByGenderItem[];
    clothStatuses: ClothStatusItem[];
}

interface CategoryByGenderItem {
    title: string;
    id: string;
    categories: CategoryItem[];
}

interface CategoryItem {
    id: string;
    title: string;
    subcategories: SubcategoryItem[];
}

interface SubcategoryItem {
    id: string;
    title: string;
}

interface ClothStatusItem {
    id: string;
    title: string;
}

interface DuplicateUserAdResponse {
    adId: string;
    firstImageUrl: string;
    brandName: string;
    modelName: string;
    size: string;
}

type CustomReason = {
    id: string;
    label: string;
    value: string;
    group: ReasonGroup;
    reasonType: ReasonType;
};

type ModerationChecklistKey =
    | 'brandModelChecked'
    | 'vintageChecked'
    | 'massMarketChecked'
    | 'forbiddenWordsChecked'
    | 'photosNoScreenshotsChecked';

const MODERATION_CHECKLIST_ITEMS: {
    key: ModerationChecklistKey;
    title: string;
    description: string;
}[] = [
    {
        key: 'brandModelChecked',
        title: 'Проверил бренд и модель',
        description: 'Без посторонних слов вроде "оригинал" и "копия"; при дубле бренда в модели выбирается подкатегория.'
    },
    {
        key: 'vintageChecked',
        title: 'Проверил винтаж',
        description: 'Убедился, что признак винтажа указан корректно.'
    },
    {
        key: 'massMarketChecked',
        title: 'Проверил fast fashion / массмаркет',
        description: 'Сверил бренд и модель с правилами по fast fashion и массмаркету.'
    },
    {
        key: 'forbiddenWordsChecked',
        title: 'Проверил описание на запрещенные слова',
        description: 'Просмотрел описание на запрещенные или вводящие в заблуждение формулировки.'
    },
    {
        key: 'photosNoScreenshotsChecked',
        title: 'Проверил фото на скрины',
        description: 'Убедился, что все фотографии товара не являются скриншотами.'
    }
];

const INITIAL_MODERATION_CHECKLIST: Record<ModerationChecklistKey, boolean> = {
    brandModelChecked: false,
    vintageChecked: false,
    massMarketChecked: false,
    forbiddenWordsChecked: false,
    photosNoScreenshotsChecked: false
};

export const AdModerationShow = () => {
    const { id } = useParams();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const theme = useTheme();
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const canOpenUserShow = canOpenPlatformUserShow(currentUser?.role);
    const { lockInfo, lockReady, lockedByAnotherUser } = useAdScreenLock('adsModeration', id);
    const [adDetail, setAdDetail] = useState<ModerationAdDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [duplicateAds, setDuplicateAds] = useState<DuplicateUserAdResponse[]>([]);
    const [duplicatesLoading, setDuplicatesLoading] = useState(false);
    const [duplicatesError, setDuplicatesError] = useState<string | null>(null);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isZoomed, setIsZoomed] = useState(false);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);
    const [selectedRejectReason, setSelectedRejectReason] = useState<string>('');
    const [rejectComment, setRejectComment] = useState<string>('');
    const [reworkDialogOpen, setReworkDialogOpen] = useState(false);
    const [reworkLoading, setReworkLoading] = useState(false);
    const [selectedReworkReason, setSelectedReworkReason] = useState<string>('');
    const [reworkComment, setReworkComment] = useState<string>('');
    const [customAdsReasons, setCustomAdsReasons] = useState<CustomReason[]>([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);
    const [expertModerationReturns, setExpertModerationReturns] = useState<ExpertModerationReturn[]>([]);
    const [expertModerationReturnsLoading, setExpertModerationReturnsLoading] = useState(false);
    const [expertModerationReturnsError, setExpertModerationReturnsError] = useState<string | null>(null);
    const [skipExpertDialogOpen, setSkipExpertDialogOpen] = useState(false);
    const [skipExpertLoading, setSkipExpertLoading] = useState(false);
    const [aiResponse, setAiResponse] = useState<string | null>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiSecondsLeft, setAiSecondsLeft] = useState(180);
    const [aiTimedOut, setAiTimedOut] = useState(false);
    const aiTimeoutRef = useRef<number | null>(null);
    const aiIntervalRef = useRef<number | null>(null);
    const aiAbortRef = useRef<AbortController | null>(null);
    const [adConfig, setAdConfig] = useState<AdConfigResponse | null>(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [configError, setConfigError] = useState<string | null>(null);
    const [editedBrand, setEditedBrand] = useState('');
    const [editedModel, setEditedModel] = useState('');
    const [selectedGenderId, setSelectedGenderId] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState('');
    const [selectedClothStatusId, setSelectedClothStatusId] = useState('');
    const [editedIsVintage, setEditedIsVintage] = useState(false);
    const [editHeroFields, setEditHeroFields] = useState(false);
    const [editDetailsFields, setEditDetailsFields] = useState(false);
    const [aiWidgetCollapsed, setAiWidgetCollapsed] = useState(false);
    const [moderationChecklist, setModerationChecklist] = useState<Record<ModerationChecklistKey, boolean>>({
        ...INITIAL_MODERATION_CHECKLIST
    });
    const [initialBrand, setInitialBrand] = useState('');
    const [initialModel, setInitialModel] = useState('');
    const [initialGenderId, setInitialGenderId] = useState('');
    const [initialCategoryId, setInitialCategoryId] = useState('');
    const [initialSubcategoryId, setInitialSubcategoryId] = useState('');
    const [initialClothStatusId, setInitialClothStatusId] = useState('');
    const [initialIsVintage, setInitialIsVintage] = useState(false);
    const [configInitialized, setConfigInitialized] = useState(false);
    const isActionLocked = !lockReady || lockedByAnotherUser;
    const lockActionHint = 'Действия недоступны, так как объявление сейчас проверяется другим пользователем.';

    const LoadingSkeleton = () => (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={2}>
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width="60%" height={40} />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="80%" />
                    </CardContent>
                </Card>
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width="50%" height={30} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                        </Box>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width="40%" height={30} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
                            <Skeleton variant="rectangular" width="100%" height={200} />
                            <Skeleton variant="rectangular" width="100%" height={200} />
                            <Skeleton variant="rectangular" width="100%" height={200} />
                        </Box>
                    </CardContent>
                </Card>
            </Stack>
        </Box>
    );

    useEffect(() => {
        if (id) {
            fetchAdDetail(id);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchAdDuplicates(id);
        }
    }, [id]);

    useEffect(() => {
        fetchAdConfig();
    }, []);

    useEffect(() => {
        if (!adDetail || !lockReady) {
            return;
        }
        loadAiSummary(adDetail, !lockedByAnotherUser);
    }, [adDetail?.id, lockReady, lockedByAnotherUser]);

    useEffect(() => {
        let isActive = true;
        const statusHistory = adDetail?.statusHistory || [];
        const previousStatus = statusHistory.length > 1
            ? statusHistory[statusHistory.length - 2]
            : '';
        const wasReturnedByExpert = previousStatus.replace(/\s+/g, '').toUpperCase() === 'EXPERT_MODERATION';

        if (!adDetail?.id || !wasReturnedByExpert) {
            setExpertModerationReturns([]);
            setExpertModerationReturnsLoading(false);
            setExpertModerationReturnsError(null);
            return () => {
                isActive = false;
            };
        }

        setExpertModerationReturns([]);
        setExpertModerationReturnsLoading(true);
        setExpertModerationReturnsError(null);

        adsProxy.getExpertModerationReturns(adDetail.id)
            .then((response) => {
                if (!isActive) {
                    return;
                }
                setExpertModerationReturns(Array.isArray(response?.items) ? response.items : []);
            })
            .catch((loadError) => {
                if (!isActive) {
                    return;
                }
                console.error('Failed to load expert moderation returns', loadError);
                setExpertModerationReturns([]);
                setExpertModerationReturnsError('Не удалось загрузить комментарии экспертов.');
            })
            .finally(() => {
                if (isActive) {
                    setExpertModerationReturnsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [adDetail]);

    useEffect(() => {
        let isActive = true;

        const loadReasons = async () => {
            setReasonsLoading(true);
            try {
                const result = await customReasonsProxy.getList();
                const reasons = ((result?.data as CustomReason[]) || [])
                    .filter((reason) => reason.group === 'ads')
                    .map((reason) => ({
                        ...reason,
                        reasonType: reason.reasonType ?? 'rework'
                    }));
                if (isActive) {
                    setCustomAdsReasons(reasons);
                }
            } catch (error) {
                console.error('Failed to load custom reasons', error);
                notify('Не удалось загрузить причины', { type: 'warning' });
            } finally {
                if (isActive) {
                    setReasonsLoading(false);
                }
            }
        };

        loadReasons();

        return () => {
            isActive = false;
        };
    }, [notify]);

    useEffect(() => {
        if (!lockedByAnotherUser) {
            return;
        }
        setApproveDialogOpen(false);
        setSkipExpertDialogOpen(false);
        setRejectDialogOpen(false);
        setReworkDialogOpen(false);
        clearAiTimers();
        if (aiAbortRef.current) {
            aiAbortRef.current.abort();
        }
        setAiLoading(false);
    }, [lockedByAnotherUser]);

    const ensureCanModerate = () => {
        if (!lockReady) {
            notify('Проверяем, кто сейчас работает с объявлением. Попробуйте еще раз.', { type: 'warning' });
            return false;
        }
        if (lockedByAnotherUser) {
            notify(`Объявление сейчас проверяется пользователем ${lockInfo?.login || 'unknown'}`, { type: 'warning' });
            return false;
        }
        return true;
    };

    const allAdsReasons = useMemo(
        () => [
            ...DEFAULT_ADS_REASONS,
            ...customAdsReasons.map((reason) => ({
                label: reason.label,
                value: reason.value,
                reasonType: reason.reasonType
            }))
        ],
        [customAdsReasons]
    );
    const rejectReasons = useMemo(
        () => allAdsReasons.filter((reason) => reason.reasonType === 'refusal'),
        [allAdsReasons]
    );
    const reworkReasons = useMemo(
        () => allAdsReasons.filter((reason) => reason.reasonType === 'rework'),
        [allAdsReasons]
    );

    const formatBool = (value: boolean) => (value ? 'да' : 'нет');

    const buildAiMessage = (detail: ModerationAdDetail) => [
        `Название (Бренд): ${detail.info.brand}`,
        `Модель: ${detail.info.model}`,
        `Цена: ${detail.info.fullPrice} рублей`,
        `Описание от продавца: ${detail.info.description}`,
        `Категория: ${detail.info.category}`,
        `Подкатегория: ${detail.info.subcategory}`,
        `Состояние: ${detail.info.clothStatus}`,
        `Размер: ${detail.info.size}`,
        `Пол: ${detail.info.gender}`,
        `Винтаж: ${formatBool(detail.info.isVintage)}`,
        `Наличие коробки: ${formatBool(detail.info.isExistBox)}`
    ].join('\n');

    const extractAiText = (data: any): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;

        const directKeys = [
            'message',
            'response',
            'result',
            'output',
            'answer',
            'content',
            'text',
            'markdown'
        ];

        for (const key of directKeys) {
            if (typeof data?.[key] === 'string') {
                return data[key];
            }
        }

        const choiceContent = data?.choices?.[0]?.message?.content || data?.choices?.[0]?.text;
        if (typeof choiceContent === 'string') {
            return choiceContent;
        }

        if (Array.isArray(data)) {
            for (const item of data) {
                const extracted = extractAiText(item);
                if (extracted) return extracted;
            }
        }

        if (typeof data === 'object') {
            const nestedKeys = ['data', 'result', 'payload'];
            for (const key of nestedKeys) {
                const extracted = extractAiText(data?.[key]);
                if (extracted) return extracted;
            }
        }

        return JSON.stringify(data, null, 2);
    };

    const normalizeAiText = (value: string) => {
        if (!value) return value;
        const hasEscapedNewlines = value.includes('\\n') && !value.includes('\n');
        const hasEscapedReturns = value.includes('\\r') && !value.includes('\r');
        if (!hasEscapedNewlines && !hasEscapedReturns && !value.includes('\\t')) {
            return value;
        }
        return value
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r')
            .replace(/\\t/g, '\t');
    };

    const clearAiTimers = () => {
        if (aiTimeoutRef.current) {
            window.clearTimeout(aiTimeoutRef.current);
            aiTimeoutRef.current = null;
        }
        if (aiIntervalRef.current) {
            window.clearInterval(aiIntervalRef.current);
            aiIntervalRef.current = null;
        }
    };

    const formatCountdown = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const restSeconds = seconds % 60;
        return `${minutes}:${String(restSeconds).padStart(2, '0')}`;
    };

    const fetchAiResponse = async (detail: ModerationAdDetail) => {
        if (lockedByAnotherUser) {
            return;
        }

        clearAiTimers();
        if (aiAbortRef.current) {
            aiAbortRef.current.abort();
        }

        const controller = new AbortController();
        aiAbortRef.current = controller;

        setAiLoading(true);
        setAiError(null);
        setAiTimedOut(false);
        setAiResponse(null);
        setAiSecondsLeft(180);

        aiIntervalRef.current = window.setInterval(() => {
            setAiSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        aiTimeoutRef.current = window.setTimeout(() => {
            controller.abort();
            setAiTimedOut(true);
            setAiLoading(false);
        }, 180000);

        try {
            const data = await aiProxy.callAgent(buildAiMessage(detail), controller.signal);
            const summary = normalizeAiText(extractAiText(data));
            setAiResponse(summary);
            if (summary.trim()) {
                try {
                    await aiSummariesProxy.saveSummary(detail.id, summary);
                } catch (saveError) {
                    console.error('Failed to save AI summary', saveError);
                }
            }
        } catch (fetchError: any) {
            if (!controller.signal.aborted) {
                setAiError(fetchError?.message || 'Ошибка при запросе к AI');
            }
        } finally {
            if (!controller.signal.aborted) {
                setAiLoading(false);
            }
            clearAiTimers();
        }
    };
    useEffect(() => {
        return () => {
            clearAiTimers();
            if (aiAbortRef.current) {
                aiAbortRef.current.abort();
            }
        };
    }, []);

    const loadAiSummary = async (detail: ModerationAdDetail, allowGeneration: boolean) => {
        if (detail.aiModeration) {
            clearAiTimers();
            if (aiAbortRef.current) {
                aiAbortRef.current.abort();
                aiAbortRef.current = null;
            }
            setAiLoading(false);
            setAiError(null);
            setAiTimedOut(false);
            setAiResponse(null);
            return;
        }

        setAiError(null);
        setAiTimedOut(false);
        try {
            const data = await aiSummariesProxy.getSummary(detail.id);
            if (typeof data?.summary === 'string' && data.summary.trim()) {
                setAiResponse(data.summary);
                return;
            }
        } catch (fetchError: any) {
            setAiError(fetchError?.message || 'Ошибка при загрузке AI ответа');
            return;
        }
        if (!allowGeneration) {
            setAiResponse(null);
            return;
        }
        fetchAiResponse(detail);
    };

    const handlePhotoClick = (index: number) => {
        setCurrentPhotoIndex(index);
        setPhotoModalOpen(true);
    };

    const handleCloseModal = () => {
        setPhotoModalOpen(false);
        setZoomLevel(1);
        setIsZoomed(false);
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(zoomLevel + 0.5, 3);
        setZoomLevel(newZoom);
        setIsZoomed(newZoom > 1);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(zoomLevel - 0.5, 0.5);
        setZoomLevel(newZoom);
        setIsZoomed(newZoom > 1);
        if (newZoom <= 1) {
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setIsZoomed(false);
        setImagePosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (event: React.MouseEvent) => {
        if (isZoomed) {
            setIsDragging(true);
            setDragStart({
                x: event.clientX - imagePosition.x,
                y: event.clientY - imagePosition.y
            });
        }
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        if (isDragging && isZoomed) {
            setImagePosition({
                x: event.clientX - dragStart.x,
                y: event.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (event: React.WheelEvent) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        
        const delta = event.deltaY > 0 ? -0.2 : 0.2;
        const newZoom = Math.max(0.5, Math.min(3, zoomLevel + delta));
        
        if (newZoom !== zoomLevel) {
            const zoomRatio = newZoom / zoomLevel;
            const newX = mouseX - (mouseX - imagePosition.x) * zoomRatio;
            const newY = mouseY - (mouseY - imagePosition.y) * zoomRatio;
            
            setZoomLevel(newZoom);
            setImagePosition({ x: newX, y: newY });
            setIsZoomed(newZoom > 1);
        }
    };

    const handlePreviousPhoto = () => {
        if (adDetail && adDetail.info.attachmentPhotoIds) {
            setCurrentPhotoIndex((prev) => 
                prev === 0 ? adDetail.info.attachmentPhotoIds.length - 1 : prev - 1
            );
            setZoomLevel(1);
            setIsZoomed(false);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleNextPhoto = () => {
        if (adDetail && adDetail.info.attachmentPhotoIds) {
            setCurrentPhotoIndex((prev) => 
                prev === adDetail.info.attachmentPhotoIds.length - 1 ? 0 : prev + 1
            );
            setZoomLevel(1);
            setIsZoomed(false);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (photoModalOpen) {
            if (event.key === 'ArrowLeft') {
                handlePreviousPhoto();
            } else if (event.key === 'ArrowRight') {
                handleNextPhoto();
            } else if (event.key === 'Escape') {
                handleCloseModal();
            } else if (event.key === '+' || event.key === '=') {
                event.preventDefault();
                handleZoomIn();
            } else if (event.key === '-') {
                event.preventDefault();
                handleZoomOut();
            } else if (event.key === '0') {
                event.preventDefault();
                handleResetZoom();
            }
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [photoModalOpen]);

    const handleApproveClick = () => {
        if (!ensureCanModerate()) return;
        setApproveDialogOpen(true);
    };

    const handleSkipExpertApproveClick = () => {
        if (!ensureCanModerate()) return;
        setSkipExpertDialogOpen(true);
    };

    const handleApproveConfirm = async () => {
        if (!ensureCanModerate()) return;
        if (!adDetail) return;
        
        setApproveLoading(true);
        try {
            const requestBody = buildRequestWithChanges({
                moderationResult: 'APPROVED'
            });
            await adsProxy.updateStatus(adDetail.id, requestBody, { moderationType: 'FIRST_MODERATION' });

            notify('Объявление успешно одобрено', { type: 'success' });
            setApproveDialogOpen(false);
            
            // Перенаправляем на список
            const path = window.location.pathname;
            if (path.includes('adsExpertModeration')) {
                redirect('list', 'adsExpertModeration');
            } else {
                redirect('list', 'adsModeration');
            }
            refresh();
        } catch (error) {
            notify('Ошибка при одобрении объявления', { type: 'error' });
        } finally {
            setApproveLoading(false);
        }
    };

    const handleApproveCancel = () => {
        setApproveDialogOpen(false);
    };

    const handleSkipExpertCancel = () => {
        setSkipExpertDialogOpen(false);
    };

    const handleSkipExpertConfirm = async () => {
        if (!ensureCanModerate()) return;
        if (!adDetail) return;

        setSkipExpertLoading(true);
        try {
            const requestBody = buildRequestWithChanges({
                moderationResult: 'APPROVED',
                skipExpertModeration: true
            });
            await adsProxy.updateStatus(adDetail.id, requestBody, { moderationType: 'FIRST_MODERATION' });

            notify('Объявление опубликовано без экспертной модерации', { type: 'success' });
            setSkipExpertDialogOpen(false);

            const path = window.location.pathname;
            if (path.includes('adsExpertModeration')) {
                redirect('list', 'adsExpertModeration');
            } else {
                redirect('list', 'adsModeration');
            }
            refresh();
        } catch (error) {
            notify('Ошибка при публикации объявления без экспертизы', { type: 'error' });
        } finally {
            setSkipExpertLoading(false);
        }
    };

    const handleRejectClick = () => {
        if (!ensureCanModerate()) return;
        setRejectDialogOpen(true);
        setSelectedRejectReason('');
        setRejectComment('');
    };

    const handleRejectConfirm = async () => {
        if (!ensureCanModerate()) return;
        if (!adDetail || !selectedRejectReason) return;
        
        setRejectLoading(true);
        try {
            const requestBody: any = {
                moderationResult: 'REJECT',
                publicReason: selectedRejectReason
            };

            if (rejectComment.trim()) {
                requestBody.internalReason = rejectComment.trim();
            } else {
                requestBody.internalReason = null;
            }

            await adsProxy.updateStatus(adDetail.id, requestBody, { moderationType: 'FIRST_MODERATION' });

            notify('Объявление отклонено', { type: 'success' });
            setRejectDialogOpen(false);
            
            // Перенаправляем на список
            const path = window.location.pathname;
            if (path.includes('adsExpertModeration')) {
                redirect('list', 'adsExpertModeration');
            } else {
                redirect('list', 'adsModeration');
            }
            refresh();
        } catch (error) {
            notify('Ошибка при отклонении объявления', { type: 'error' });
        } finally {
            setRejectLoading(false);
        }
    };

    const handleRejectCancel = () => {
        setRejectDialogOpen(false);
        setSelectedRejectReason('');
        setRejectComment('');
    };

    const handleReworkClick = () => {
        if (!ensureCanModerate()) return;
        setReworkDialogOpen(true);
        setSelectedReworkReason('');
        setReworkComment('');
    };

    const handleReworkConfirm = async () => {
        if (!ensureCanModerate()) return;
        if (!adDetail || !selectedReworkReason) return;
        
        setReworkLoading(true);
        try {
            const requestBody: any = {
                moderationResult: 'REQUEST_CORRECT_INFO',
                publicReason: selectedReworkReason
            };

            if (reworkComment.trim()) {
                requestBody.internalReason = reworkComment.trim();
            } else {
                requestBody.internalReason = null;
            }

            await adsProxy.updateStatus(adDetail.id, requestBody, { moderationType: 'FIRST_MODERATION' });

            notify('Объявление отправлено на доработку', { type: 'success' });
            setReworkDialogOpen(false);
            
            // Перенаправляем на список
            const path = window.location.pathname;
            if (path.includes('adsExpertModeration')) {
                redirect('list', 'adsExpertModeration');
            } else {
                redirect('list', 'adsModeration');
            }
            refresh();
        } catch (error) {
            notify('Ошибка при отправке на доработку', { type: 'error' });
        } finally {
            setReworkLoading(false);
        }
    };

    const handleReworkCancel = () => {
        setReworkDialogOpen(false);
        setSelectedReworkReason('');
        setReworkComment('');
    };

    const fetchAdDetail = async (adId: string) => {
        setDetailLoading(true);
        try {
            const data: ApiResponse = await adsProxy.getOne(adId);
            if (data.success) {
                setAdDetail(data.body);
            } else {
                throw new Error('Ошибка при получении данных');
            }
        } catch (error) {
            setError('Ошибка при загрузке данных объявления');
            notify('Ошибка при загрузке данных объявления', { type: 'error' });
        } finally {
            setDetailLoading(false);
        }
    };

    const fetchAdDuplicates = async (adId: string) => {
        setDuplicatesLoading(true);
        setDuplicatesError(null);
        try {
            const data: any = await adsProxy.getDuplicates(adId);
            const responseBody = Array.isArray(data?.body)
                ? data.body
                : Array.isArray(data)
                    ? data
                    : [];
            setDuplicateAds(responseBody as DuplicateUserAdResponse[]);
        } catch (error) {
            setDuplicateAds([]);
            setDuplicatesError('Не удалось загрузить дубликаты');
            notify('Не удалось загрузить дубликаты', { type: 'warning' });
        } finally {
            setDuplicatesLoading(false);
        }
    };

    const fetchAdConfig = async () => {
        setConfigLoading(true);
        setConfigError(null);
        try {
            const data: any = await adsProxy.getAdConfig();
            const payload = data?.body ?? data;
            const categoriesByGender = Array.isArray(payload?.categoriesByGender)
                ? payload.categoriesByGender
                : [];
            const clothStatuses = Array.isArray(payload?.clothStatuses)
                ? payload.clothStatuses
                : [];
            if (!categoriesByGender.length && !clothStatuses.length) {
                throw new Error('empty config');
            }
            setAdConfig({
                categoriesByGender,
                clothStatuses
            });
        } catch (error) {
            setConfigError('Не удалось загрузить справочники для модерации');
            setAdConfig(null);
        } finally {
            setConfigLoading(false);
        }
    };

    useEffect(() => {
        if (!adDetail) return;
        setEditedBrand(adDetail.info.brand || '');
        setEditedModel(adDetail.info.model || '');
        setEditedIsVintage(Boolean(adDetail.info.isVintage));
        setInitialBrand(adDetail.info.brand || '');
        setInitialModel(adDetail.info.model || '');
        setInitialIsVintage(Boolean(adDetail.info.isVintage));
        setSelectedGenderId('');
        setSelectedCategoryId('');
        setSelectedSubcategoryId('');
        setSelectedClothStatusId('');
        setInitialGenderId('');
        setInitialCategoryId('');
        setInitialSubcategoryId('');
        setInitialClothStatusId('');
        setConfigInitialized(false);
        setEditHeroFields(false);
        setEditDetailsFields(false);
        setAiWidgetCollapsed(false);
        setModerationChecklist({ ...INITIAL_MODERATION_CHECKLIST });
    }, [adDetail?.id]);

    useEffect(() => {
        if (!adDetail || !adConfig || configInitialized) return;

        const matchByIdOrTitle = (value: string, item: { id: string; title: string }) =>
            item.id === value || item.title === value;

        const categoriesByGender = adConfig?.categoriesByGender ?? [];
        const clothStatuses = adConfig?.clothStatuses ?? [];

        const genderMatch = categoriesByGender.find((gender) =>
            matchByIdOrTitle(adDetail.info.gender, gender)
        );

        const categoryMatch = categoriesByGender.flatMap((gender) =>
            gender.categories.map((category) => ({ gender, category }))
        ).find(({ category }) => matchByIdOrTitle(adDetail.info.category, category));

        const subcategoryMatch = categoriesByGender.flatMap((gender) =>
            gender.categories.flatMap((category) =>
                category.subcategories.map((subcategory) => ({ gender, category, subcategory }))
            )
        ).find(({ subcategory }) => matchByIdOrTitle(adDetail.info.subcategory, subcategory));

        const resolvedGenderId = genderMatch?.id || categoryMatch?.gender.id || subcategoryMatch?.gender.id || '';
        const genderCategories = categoriesByGender.find((gender) => gender.id === resolvedGenderId)?.categories || [];
        const resolvedCategoryId = genderCategories.find((category) => matchByIdOrTitle(adDetail.info.category, category))?.id
            || categoryMatch?.category.id
            || subcategoryMatch?.category.id
            || '';
        const categorySubcategories = genderCategories.find((category) => category.id === resolvedCategoryId)?.subcategories || [];
        const resolvedSubcategoryId = categorySubcategories.find((subcategory) => matchByIdOrTitle(adDetail.info.subcategory, subcategory))?.id
            || subcategoryMatch?.subcategory.id
            || '';
        const resolvedClothStatusId = clothStatuses.find((status) =>
            matchByIdOrTitle(adDetail.info.clothStatus, status)
        )?.id || '';

        setSelectedGenderId(resolvedGenderId);
        setSelectedCategoryId(resolvedCategoryId);
        setSelectedSubcategoryId(resolvedSubcategoryId);
        setSelectedClothStatusId(resolvedClothStatusId);
        setInitialGenderId(resolvedGenderId);
        setInitialCategoryId(resolvedCategoryId);
        setInitialSubcategoryId(resolvedSubcategoryId);
        setInitialClothStatusId(resolvedClothStatusId);
        setConfigInitialized(true);
    }, [adDetail, adConfig, configInitialized]);

    const buildModerationChangedFields = () => {
        if (!adDetail) return null;

        const changes: Record<string, any> = {};

        if (editedBrand !== initialBrand) {
            changes.newBrandName = editedBrand;
        }
        if (editedModel !== initialModel) {
            changes.newModelName = editedModel;
        }
        if (selectedGenderId && selectedGenderId !== initialGenderId) {
            changes.newGender = selectedGenderId;
        }
        if (selectedCategoryId && selectedCategoryId !== initialCategoryId) {
            changes.newCategory = selectedCategoryId;
        }
        if (selectedSubcategoryId && selectedSubcategoryId !== initialSubcategoryId) {
            changes.newSubcategory = selectedSubcategoryId;
        }
        if (selectedClothStatusId && selectedClothStatusId !== initialClothStatusId) {
            changes.newClothStatus = selectedClothStatusId;
        }
        if (editedIsVintage !== initialIsVintage) {
            changes.newIsVintage = editedIsVintage;
        }

        return Object.keys(changes).length ? changes : null;
    };

    const buildRequestWithChanges = (basePayload: Record<string, any>) => {
        const moderationChangedFields = buildModerationChangedFields();
        if (!moderationChangedFields) {
            return basePayload;
        }
        return {
            ...basePayload,
            moderationChangedFields
        };
    };

    const hasModerationChanges = Boolean(buildModerationChangedFields());
    const genderOptions = adConfig?.categoriesByGender ?? [];
    const selectedGender = genderOptions.find((gender) => gender.id === selectedGenderId);
    const categoryOptions = selectedGender?.categories || [];
    const selectedCategory = categoryOptions.find((category) => category.id === selectedCategoryId);
    const subcategoryOptions = selectedCategory?.subcategories || [];
    const clothStatusOptions = adConfig?.clothStatuses ?? [];
    const configReady = Boolean(adConfig && !configLoading && !configError);
    const allCategories = genderOptions.flatMap((gender) => gender.categories);
    const allSubcategories = allCategories.flatMap((category) => category.subcategories);

    const formatTextValue = (value: string) => value?.trim() ? value.trim() : '—';
    const formatBoolValue = (value: boolean) => (value ? 'да' : 'нет');
    const formatPriceValue = (value: number) => Number(value || 0).toLocaleString('ru-RU');
    const resolveGenderTitle = (value: string) => genderOptions.find((gender) => gender.id === value)?.title || formatTextValue(value);
    const resolveCategoryTitle = (value: string) => allCategories.find((category) => category.id === value)?.title || formatTextValue(value);
    const resolveSubcategoryTitle = (value: string) => allSubcategories.find((subcategory) => subcategory.id === value)?.title || formatTextValue(value);
    const resolveClothStatusTitle = (value: string) => clothStatusOptions.find((status) => status.id === value)?.title || formatTextValue(value);
    const resolveStatusTitle = (value: string) => {
        switch (value) {
            case 'MODERATION':
                return 'На модерации';
            case 'EXPERT_MODERATION':
                return 'Экспертная модерация';
            case 'APPROVED':
                return 'Одобрено';
            case 'REJECTED':
                return 'Отклонено';
            default:
                return value;
        }
    };
    const normalizeComparableValue = (value?: string) => value?.replace(/\s+/g, '').toUpperCase() || '';

    const moderationChangeSummary = [
        editedBrand !== initialBrand
            ? { label: 'Бренд', from: formatTextValue(initialBrand), to: formatTextValue(editedBrand) }
            : null,
        editedModel !== initialModel
            ? { label: 'Модель', from: formatTextValue(initialModel), to: formatTextValue(editedModel) }
            : null,
        selectedGenderId && selectedGenderId !== initialGenderId
            ? { label: 'Пол', from: resolveGenderTitle(initialGenderId), to: resolveGenderTitle(selectedGenderId) }
            : null,
        selectedCategoryId && selectedCategoryId !== initialCategoryId
            ? { label: 'Категория', from: resolveCategoryTitle(initialCategoryId), to: resolveCategoryTitle(selectedCategoryId) }
            : null,
        selectedSubcategoryId && selectedSubcategoryId !== initialSubcategoryId
            ? { label: 'Подкатегория', from: resolveSubcategoryTitle(initialSubcategoryId), to: resolveSubcategoryTitle(selectedSubcategoryId) }
            : null,
        selectedClothStatusId && selectedClothStatusId !== initialClothStatusId
            ? { label: 'Состояние', from: resolveClothStatusTitle(initialClothStatusId), to: resolveClothStatusTitle(selectedClothStatusId) }
            : null,
        editedIsVintage !== initialIsVintage
            ? { label: 'Винтаж', from: formatBoolValue(initialIsVintage), to: formatBoolValue(editedIsVintage) }
            : null
    ].filter(Boolean) as { label: string; from: string; to: string }[];
    const completedChecklistCount = MODERATION_CHECKLIST_ITEMS.filter((item) => moderationChecklist[item.key]).length;
    const isModerationChecklistComplete = completedChecklistCount === MODERATION_CHECKLIST_ITEMS.length;
    const moderationActionDisabled = isActionLocked || !isModerationChecklistComplete;
    const moderationActionHint = lockedByAnotherUser
        ? lockActionHint
        : !isModerationChecklistComplete
            ? 'Заполните чек-лист перед принятием решения.'
            : '';



    if (detailLoading) {
        return (
            <Box>
                <Title title="Детали объявления" />
                <Box sx={{ mt: 2 }}>
                    <LoadingSkeleton />
                </Box>
            </Box>
        );
    }

    if (error) {
        notify('Ошибка при загрузке данных', { type: 'error' });
        return <div>Ошибка при загрузке данных</div>;
    }

    if (!adDetail) return <div>Данные не найдены</div>;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('ru-RU');
    };
    const formatExpertReturnAuthor = (item: ExpertModerationReturn) => {
        if (item.expert.fullName && item.expert.login) {
            return `${item.expert.fullName} (${item.expert.login})`;
        }
        return item.expert.fullName || item.expert.login || 'Неизвестный эксперт';
    };
    const userIsInactive = adDetail.userInfo.isActive === false;
    const hasDuplicates = duplicateAds.length > 0;
    const currentStatusLabel = resolveStatusTitle(adDetail.currentStatus);
    const currentAdSizeLabel = formatTextValue(adDetail.info.size);
    const hasPreviousModerationStatus = adDetail.statusHistory.some(
        (status, index) =>
            status === 'MODERATION'
            && !(status === adDetail.currentStatus && index === adDetail.statusHistory.length - 1)
    );
    const previousStatusInHistory = adDetail.statusHistory.length > 1
        ? adDetail.statusHistory[adDetail.statusHistory.length - 2]
        : '';
    const isSendToExpertsBlockedByExpertReturn = normalizeComparableValue(previousStatusInHistory) === 'EXPERT_MODERATION';
    const sendToExpertsActionDisabled = moderationActionDisabled || isSendToExpertsBlockedByExpertReturn;
    const sendToExpertsActionHint = lockedByAnotherUser
        ? lockActionHint
        : !isModerationChecklistComplete
            ? 'Заполните чек-лист перед принятием решения.'
            : isSendToExpertsBlockedByExpertReturn
                ? 'Эксперты вернули это объявление обратно модераторам. Повторная отправка экспертам недоступна.'
                : '';
    const checklistProgressValue = (completedChecklistCount / MODERATION_CHECKLIST_ITEMS.length) * 100;
    const isDarkMode = theme.palette.mode === 'dark';
    const userRatingColor =
        adDetail.userInfo.rating >= 4 && adDetail.userInfo.rating <= 5
            ? 'success.main'
            : adDetail.userInfo.rating >= 3 && adDetail.userInfo.rating < 4
                ? 'warning.main'
                : 'error.main';
    const heroCardBorderColor = alpha(theme.palette.warning.main, isDarkMode ? 0.4 : 0.22);
    const heroCardBackground = isDarkMode
        ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%, ${alpha(theme.palette.info.main, 0.16)} 100%)`
        : `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.26)} 0%, ${theme.palette.background.paper} 58%, ${alpha(theme.palette.info.light, 0.22)} 100%)`;
    const brandHighlightBg = alpha(theme.palette.warning.main, isDarkMode ? 0.18 : 0.08);
    const brandHighlightBorder = `1px solid ${alpha(theme.palette.warning.main, isDarkMode ? 0.34 : 0.18)}`;
    const modelHighlightBg = alpha(theme.palette.info.main, isDarkMode ? 0.2 : 0.08);
    const modelHighlightBorder = `1px solid ${alpha(theme.palette.info.main, isDarkMode ? 0.36 : 0.18)}`;
    const pricePanelBg = isDarkMode ? alpha(theme.palette.common.black, 0.28) : theme.palette.grey[900];
    const pricePanelTextColor = isDarkMode ? theme.palette.text.primary : theme.palette.common.white;
    const pricePanelChipBg = isDarkMode ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.white, 0.12);
    const softSurfaceBg = isDarkMode ? alpha(theme.palette.background.paper, 0.82) : alpha(theme.palette.common.white, 0.74);
    const softSurfaceBorder = `1px solid ${alpha(theme.palette.divider, isDarkMode ? 1 : 0.6)}`;
    const descriptionBg = isDarkMode ? alpha(theme.palette.background.paper, 0.9) : alpha(theme.palette.common.white, 0.88);
    const statItemBg = isDarkMode ? alpha(theme.palette.background.default, 0.42) : theme.palette.grey[50];
    const statItemBorder = `1px solid ${alpha(theme.palette.divider, isDarkMode ? 1 : 0.75)}`;
    const duplicateImageBg = isDarkMode ? alpha(theme.palette.common.white, 0.05) : theme.palette.grey[100];
    const duplicateMatchBg = alpha(theme.palette.warning.main, isDarkMode ? 0.18 : 0.1);
    const duplicateErrorBg = alpha(theme.palette.error.main, isDarkMode ? 0.18 : 0.08);
    const avatarFallbackBg = isDarkMode ? alpha(theme.palette.common.white, 0.1) : theme.palette.grey[300];
    const checklistCompleteBg = alpha(theme.palette.success.main, isDarkMode ? 0.16 : 0.08);
    const checklistDefaultBg = isDarkMode ? alpha(theme.palette.background.default, 0.42) : theme.palette.grey[50];
    const aiWidgetBorder = alpha(theme.palette.info.main, isDarkMode ? 0.42 : 0.24);
    const aiWidgetBackground = isDarkMode
        ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.22)} 0%, ${alpha(theme.palette.background.paper, 0.94)} 52%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`
        : `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.28)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 52%, ${alpha(theme.palette.secondary.light, 0.18)} 100%)`;
    const isDuplicateSizeMismatch = (duplicateSize: string) => {
        const currentSize = normalizeComparableValue(adDetail.info.size);
        const comparedSize = normalizeComparableValue(duplicateSize);
        if (!currentSize || !comparedSize) {
            return false;
        }
        return currentSize !== comparedSize;
    };
    const openDuplicateAd = (duplicateAdId: string) => {
        if (!duplicateAdId) {
            return;
        }
        redirect('show', 'userAds', duplicateAdId);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
            <Title title={`Модерация: объявление ${adDetail.id}`} />
            {userIsInactive && (
                <Box
                    sx={{
                        position: 'fixed',
                        right: { xs: 12, md: 24 },
                        top: { xs: 12, md: 24 },
                        zIndex: (theme) => theme.zIndex.snackbar
                    }}
                >
                    <Alert
                        severity="error"
                        sx={{
                            width: { xs: 'calc(100vw - 24px)', sm: 520 },
                            maxWidth: '95vw',
                            boxShadow: 6,
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Пользователь удален
                        </Typography>
                        <Typography variant="body1">
                            Аккаунт пользователя неактивен (удален или заблокирован).
                        </Typography>
                    </Alert>
                </Box>
            )}
            {lockedByAnotherUser && (
                <Box
                    sx={{
                        position: 'fixed',
                        right: { xs: 12, md: 24 },
                        bottom: { xs: 12, md: 24 },
                        zIndex: (theme) => theme.zIndex.snackbar
                    }}
                >
                    <Alert
                        severity="warning"
                        sx={{
                            width: { xs: 'calc(100vw - 24px)', sm: 520 },
                            maxWidth: '95vw',
                            boxShadow: 6,
                            borderRadius: 2,
                            alignItems: 'flex-start',
                            '& .MuiAlert-icon': {
                                margin: 0,
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'flex-start',
                                mt: '2px'
                            },
                            '& .MuiAlert-message': {
                                fontSize: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start'
                            }
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Объявление занято
                        </Typography>
                        <Typography variant="body1">
                            Объявление сейчас проверяется пользователем {lockInfo?.login || 'unknown'}. Некоторые действия могут быть отключены
                        </Typography>
                    </Alert>
                </Box>
            )}
            <Card
                sx={{
                    mt: 2,
                    border: '1px solid',
                    borderColor: heroCardBorderColor,
                    background: heroCardBackground,
                    overflow: 'hidden'
                }}
            >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`Статус: ${currentStatusLabel}`} color="warning" variant="outlined" size="small" />
                        <Chip label={`ID: ${adDetail.id}`} variant="outlined" size="small" />
                        {hasPreviousModerationStatus && (
                            <Chip label="Повторная модерация" color="info" variant="filled" size="small" />
                        )}
                        {userIsInactive && <Chip label="Пользователь неактивен" color="error" size="small" />}
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="overline" sx={{ color: 'warning.dark', letterSpacing: '0.08em' }}>
                                    Критические поля для проверки
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                                    Сначала проверьте бренд, модель, цену и описание
                                </Typography>
                            </Box>
                            <Tooltip title="Редактировать бренд и модель">
                                <span>
                                    <IconButton
                                        onClick={() => setEditHeroFields((prev) => !prev)}
                                        color={editHeroFields ? 'warning' : 'default'}
                                        disabled={isActionLocked}
                                    >
                                        <EditOutlinedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(220px, 280px)' },
                            gap: 1.5,
                            alignItems: 'stretch'
                        }}
                    >
                        <Box sx={{ p: 2, borderRadius: 3, bgcolor: brandHighlightBg, border: brandHighlightBorder }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Бренд
                            </Typography>
                            {editHeroFields ? (
                                <TextField
                                    value={editedBrand}
                                    onChange={(event) => setEditedBrand(event.target.value)}
                                    fullWidth
                                    size="small"
                                    disabled={isActionLocked}
                                />
                            ) : (
                                <Typography variant="h4" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                                    {formatTextValue(adDetail.info.brand)}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ p: 2, borderRadius: 3, bgcolor: modelHighlightBg, border: modelHighlightBorder }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Модель
                            </Typography>
                            {editHeroFields ? (
                                <TextField
                                    value={editedModel}
                                    onChange={(event) => setEditedModel(event.target.value)}
                                    fullWidth
                                    size="small"
                                    disabled={isActionLocked}
                                />
                            ) : (
                                <Typography variant="h4" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                                    {formatTextValue(adDetail.info.model)}
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                bgcolor: pricePanelBg,
                                color: pricePanelTextColor,
                                boxShadow: 4
                            }}
                        >
                            <Typography variant="overline" sx={{ opacity: 0.72, letterSpacing: '0.08em' }}>
                                Цена и размер
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 2 }}>
                                {formatPriceValue(adDetail.info.fullPrice)} ₽
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                    label={`Размер: ${currentAdSizeLabel}`}
                                    sx={{ bgcolor: pricePanelChipBg, color: pricePanelTextColor }}
                                />
                                <Chip
                                    label={`Состояние: ${formatTextValue(adDetail.info.clothStatus)}`}
                                    sx={{ bgcolor: pricePanelChipBg, color: pricePanelTextColor }}
                                />
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 2.5, opacity: 0.78 }}>
                                {`${formatTextValue(adDetail.info.city)}, ${formatTextValue(adDetail.info.country)}`}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            mt: 2,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.5
                        }}
                    >
                        <Box sx={{ p: 2, borderRadius: 3, bgcolor: softSurfaceBg, border: softSurfaceBorder }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Категория
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                {formatTextValue(adDetail.info.category)}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                {formatTextValue(adDetail.info.subcategory)}
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: 3, bgcolor: softSurfaceBg, border: softSurfaceBorder }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Дополнительные признаки
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                <Chip label={`Винтаж: ${formatBoolValue(adDetail.info.isVintage)}`} color={adDetail.info.isVintage ? 'success' : 'default'} size="small" />
                                <Chip label={`Обмен: ${formatBoolValue(adDetail.info.isExchange)}`} color={adDetail.info.isExchange ? 'success' : 'default'} size="small" />
                                <Chip label={`Коробка: ${formatBoolValue(adDetail.info.isExistBox)}`} color={adDetail.info.isExistBox ? 'success' : 'default'} size="small" />
                            </Stack>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2, p: 2.5, borderRadius: 3, bgcolor: descriptionBg, border: '1px solid', borderColor: heroCardBorderColor }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'warning.dark' }}>
                                Описание
                            </Typography>
                            {!adDetail.info.description?.trim() && <Chip label="Описание отсутствует" color="error" size="small" />}
                        </Box>
                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                fontSize: { xs: '1rem', md: '1.05rem' },
                                lineHeight: 1.75,
                                fontWeight: 500
                            }}
                        >
                            {formatTextValue(adDetail.info.description)}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            <AiModerationResultCard aiModeration={adDetail.aiModeration} sx={{ mt: 2 }} />

            <Box
                sx={{
                    mt: 2,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.7fr) minmax(320px, 380px)' },
                    gap: 2,
                    alignItems: 'start'
                }}
            >
                <Stack spacing={2} sx={{ order: { xs: 2, lg: 1 }, minWidth: 0 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h6">
                                    Основные характеристики
                                </Typography>
                                <Tooltip title="Редактировать характеристики">
                                    <span>
                                        <IconButton
                                            onClick={() => setEditDetailsFields((prev) => !prev)}
                                            color={editDetailsFields ? 'warning' : 'default'}
                                            disabled={isActionLocked}
                                        >
                                            <EditOutlinedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            {editDetailsFields && configLoading && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Загружаю справочники...
                                </Typography>
                            )}
                            {editDetailsFields && configError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {configError}
                                </Alert>
                            )}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                                    gap: 1.5
                                }}
                            >
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Пол
                                    </Typography>
                                    {editDetailsFields ? (
                                        <FormControl fullWidth size="small" disabled={!configReady || isActionLocked} sx={{ mt: 1 }}>
                                            <InputLabel id="inline-gender-select-label">Пол</InputLabel>
                                            <Select
                                                labelId="inline-gender-select-label"
                                                label="Пол"
                                                value={selectedGenderId}
                                                onChange={(event) => {
                                                    const nextGenderId = String(event.target.value);
                                                    setSelectedGenderId(nextGenderId);
                                                    const nextCategories = genderOptions.find((gender) => gender.id === nextGenderId)?.categories || [];
                                                    if (!nextCategories.some((category) => category.id === selectedCategoryId)) {
                                                        setSelectedCategoryId('');
                                                        setSelectedSubcategoryId('');
                                                    }
                                                }}
                                            >
                                                {genderOptions.map((gender) => (
                                                    <MenuItem key={gender.id} value={gender.id}>
                                                        {gender.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                            {formatTextValue(adDetail.info.gender)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Категория
                                    </Typography>
                                    {editDetailsFields ? (
                                        <FormControl fullWidth size="small" disabled={!configReady || !selectedGenderId || isActionLocked} sx={{ mt: 1 }}>
                                            <InputLabel id="inline-category-select-label">Категория</InputLabel>
                                            <Select
                                                labelId="inline-category-select-label"
                                                label="Категория"
                                                value={selectedCategoryId}
                                                onChange={(event) => {
                                                    const nextCategoryId = String(event.target.value);
                                                    setSelectedCategoryId(nextCategoryId);
                                                    const nextSubcategories = categoryOptions.find((category) => category.id === nextCategoryId)?.subcategories || [];
                                                    if (!nextSubcategories.some((subcategory) => subcategory.id === selectedSubcategoryId)) {
                                                        setSelectedSubcategoryId('');
                                                    }
                                                }}
                                            >
                                                {categoryOptions.map((category) => (
                                                    <MenuItem key={category.id} value={category.id}>
                                                        {category.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                            {formatTextValue(adDetail.info.category)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Подкатегория
                                    </Typography>
                                    {editDetailsFields ? (
                                        <FormControl fullWidth size="small" disabled={!configReady || !selectedCategoryId || isActionLocked} sx={{ mt: 1 }}>
                                            <InputLabel id="inline-subcategory-select-label">Подкатегория</InputLabel>
                                            <Select
                                                labelId="inline-subcategory-select-label"
                                                label="Подкатегория"
                                                value={selectedSubcategoryId}
                                                onChange={(event) => setSelectedSubcategoryId(String(event.target.value))}
                                            >
                                                {subcategoryOptions.map((subcategory) => (
                                                    <MenuItem key={subcategory.id} value={subcategory.id}>
                                                        {subcategory.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                            {formatTextValue(adDetail.info.subcategory)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Размер
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                        {currentAdSizeLabel}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Состояние
                                    </Typography>
                                    {editDetailsFields ? (
                                        <FormControl fullWidth size="small" disabled={!configReady || isActionLocked} sx={{ mt: 1 }}>
                                            <InputLabel id="inline-cloth-status-select-label">Состояние</InputLabel>
                                            <Select
                                                labelId="inline-cloth-status-select-label"
                                                label="Состояние"
                                                value={selectedClothStatusId}
                                                onChange={(event) => setSelectedClothStatusId(String(event.target.value))}
                                            >
                                                {clothStatusOptions.map((status) => (
                                                    <MenuItem key={status.id} value={status.id}>
                                                        {status.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                            {formatTextValue(adDetail.info.clothStatus)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Винтаж
                                    </Typography>
                                    {editDetailsFields ? (
                                        <FormControlLabel
                                            sx={{ mt: 0.75 }}
                                            control={
                                                <Switch
                                                    checked={editedIsVintage}
                                                    onChange={(event) => setEditedIsVintage(event.target.checked)}
                                                    disabled={isActionLocked}
                                                />
                                            }
                                            label={editedIsVintage ? 'Да' : 'Нет'}
                                        />
                                    ) : (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                            {formatBoolValue(adDetail.info.isVintage)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: statItemBg, border: statItemBorder }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Серийный номер
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
                                        {formatTextValue(adDetail.info.serialNumber || '')}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
                        <CardContent sx={{ minWidth: 0, overflowX: 'hidden' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.25,
                                    mb: 2,
                                    minWidth: 0,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Typography variant="h6" sx={{ flexShrink: 0 }}>
                                    Дупликаты
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                                    <Chip label={`Текущий размер: ${currentAdSizeLabel}`} size="small" color="warning" variant="outlined" />
                                    {!duplicatesLoading && !duplicatesError && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                                            <InfoIcon sx={{ color: hasDuplicates ? 'error.main' : 'success.main' }} />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: hasDuplicates ? 'error.main' : 'text.secondary',
                                                    fontWeight: hasDuplicates ? 700 : 500,
                                                    overflowWrap: 'anywhere'
                                                }}
                                            >
                                                {hasDuplicates ? 'Обнаружены дупликаты, сравните размер в каждой карточке' : 'Дупликаты не обнаружены'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            {duplicatesLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Проверяем наличие дубликатов...
                                    </Typography>
                                </Box>
                            ) : duplicatesError ? (
                                <Alert severity="warning">{duplicatesError}</Alert>
                            ) : hasDuplicates ? (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                        gap: 1.5,
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0
                                    }}
                                >
                                    {duplicateAds.map((duplicate) => {
                                        const sizeMismatch = isDuplicateSizeMismatch(duplicate.size);

                                        return (
                                            <Card
                                                key={duplicate.adId}
                                                variant="outlined"
                                                onClick={() => openDuplicateAd(duplicate.adId)}
                                                sx={{
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    borderColor: sizeMismatch ? 'error.light' : 'divider',
                                                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: 4,
                                                        transform: 'translateY(-1px)'
                                                    }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: 140,
                                                        bgcolor: duplicateImageBg,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    {duplicate.firstImageUrl ? (
                                                        <Box
                                                            component="img"
                                                            src={duplicate.firstImageUrl}
                                                            alt={`Дубликат ${duplicate.adId}`}
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Фото отсутствует
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <CardContent sx={{ display: 'grid', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Бренд
                                                        </Typography>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                                            {formatTextValue(duplicate.brandName)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Модель
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                                            {formatTextValue(duplicate.modelName)}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            p: 1.25,
                                                            borderRadius: 2,
                                                            bgcolor: sizeMismatch ? duplicateErrorBg : duplicateMatchBg,
                                                            border: '1px solid',
                                                            borderColor: sizeMismatch ? 'error.light' : 'warning.light'
                                                        }}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            Размер дубликата
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 800,
                                                                color: sizeMismatch ? 'error.main' : 'text.primary',
                                                                mt: 0.25
                                                            }}
                                                        >
                                                            {formatTextValue(duplicate.size)}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 0.5,
                                                                color: sizeMismatch ? 'error.main' : 'warning.main'
                                                            }}
                                                        >
                                                            {duplicate.size
                                                                ? sizeMismatch
                                                                    ? `Не совпадает с текущим размером: ${currentAdSizeLabel}`
                                                                    : 'Совпадает с текущим объявлением'
                                                                : 'Размер дубликата не указан'}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Alert severity="success">Дубликаты не обнаружены.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    {adDetail.info.attachmentPhotoIds && adDetail.info.attachmentPhotoIds.length > 0 && (
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                                    <Box>
                                        <Typography variant="h6">Фотографии товара</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Все превью показываются целиком без обрезки, чтобы не пропустить скриншоты.
                                        </Typography>
                                    </Box>
                                    <Chip label={`${adDetail.info.attachmentPhotoIds.length} фото`} size="small" variant="outlined" />
                                </Box>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                            lg: 'repeat(4, 1fr)'
                                        },
                                        gap: 2
                                    }}
                                >
                                    {adDetail.info.attachmentPhotoIds.map((url: string, index: number) => (
                                        <Card
                                            key={index}
                                            onClick={() => handlePhotoClick(index)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    opacity: 0.88
                                                },
                                                width: '100%',
                                                p: 1.5,
                                                bgcolor: duplicateImageBg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minHeight: 260
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={url}
                                                alt={`Фото товара ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    maxHeight: 420,
                                                    objectFit: 'contain',
                                                    display: 'block'
                                                }}
                                            />
                                        </Card>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                </Stack>

                <Stack spacing={2} sx={{ order: { xs: 1, lg: 2 }, minWidth: 0 }}>
                    <Card sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => {
                                        const path = window.location.pathname;
                                        if (path.includes('adsExpertModeration')) {
                                            redirect('list', 'adsExpertModeration');
                                        } else {
                                            redirect('list', 'adsModeration');
                                        }
                                    }}
                                >
                                    Назад к списку
                                </Button>

                                <Box>
                                    <Typography variant="h6">Чек-лист модератора</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Пока чек-лист не заполнен, кнопки решения по объявлению заблокированы.
                                    </Typography>
                                </Box>

                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle2">Прогресс проверки</Typography>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {completedChecklistCount}/{MODERATION_CHECKLIST_ITEMS.length}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={checklistProgressValue}
                                        sx={{ height: 8, borderRadius: 999 }}
                                    />
                                </Box>

                                <Stack spacing={1}>
                                    {MODERATION_CHECKLIST_ITEMS.map((item) => (
                                        <Box
                                            key={item.key}
                                            sx={{
                                                p: 1.25,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: moderationChecklist[item.key] ? 'success.light' : 'divider',
                                                bgcolor: moderationChecklist[item.key] ? checklistCompleteBg : checklistDefaultBg
                                            }}
                                        >
                                            <FormControlLabel
                                                sx={{
                                                    m: 0,
                                                    width: '100%',
                                                    alignItems: 'flex-start',
                                                    '& .MuiCheckbox-root': {
                                                        pt: 0.25,
                                                        pr: 1.5
                                                    }
                                                }}
                                                control={
                                                    <Checkbox
                                                        checked={moderationChecklist[item.key]}
                                                        onChange={(event) =>
                                                            setModerationChecklist((prev) => ({
                                                                ...prev,
                                                                [item.key]: event.target.checked
                                                            }))
                                                        }
                                                        disabled={isActionLocked}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            {item.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                            {item.description}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Stack>

                                {!isModerationChecklistComplete && !lockedByAnotherUser && (
                                    <Alert severity="warning">
                                        Заполните чек-лист, чтобы открыть кнопки модерации.
                                    </Alert>
                                )}

                                {hasModerationChanges && (
                                    <Alert severity="info">
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            Модератор изменил поля
                                        </Typography>
                                        <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                                            {moderationChangeSummary.map((entry) => (
                                                <li key={entry.label}>
                                                    <Typography variant="body2">
                                                        <strong>{entry.label}:</strong> {entry.from} → {entry.to}
                                                    </Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Alert>
                                )}

                                <Alert severity="info" icon={<InfoIcon />}>
                                    Сфокусируйтесь на бренде, модели, описании, цене и размере в блоке дубликатов.
                                </Alert>

                                {isSendToExpertsBlockedByExpertReturn && (
                                    <Alert severity="warning">
                                        <Typography variant="body2">
                                            Эксперты вернули это объявление обратно модераторам, поэтому повторная отправка экспертам недоступна.
                                        </Typography>
                                        {expertModerationReturnsLoading && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                                                <CircularProgress size={18} />
                                                <Typography variant="body2">Загружаем комментарии экспертов...</Typography>
                                            </Box>
                                        )}
                                        {expertModerationReturnsError && (
                                            <Typography variant="body2" color="error.main" sx={{ mt: 1.5 }}>
                                                {expertModerationReturnsError}
                                            </Typography>
                                        )}
                                        {!expertModerationReturnsLoading
                                            && !expertModerationReturnsError
                                            && expertModerationReturns.length === 0 && (
                                            <Typography variant="body2" sx={{ mt: 1.5 }}>
                                                Комментарии к возврату отсутствуют.
                                            </Typography>
                                        )}
                                        {expertModerationReturns.length > 0 && (
                                            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                                                {expertModerationReturns.map((returnItem) => (
                                                    <Box
                                                        key={returnItem.id}
                                                        sx={{
                                                            borderTop: `1px solid ${alpha(theme.palette.warning.dark, 0.25)}`,
                                                            pt: 1.25
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {formatExpertReturnAuthor(returnItem)}
                                                            {' · '}
                                                            {returnItem.createdAt ? formatDate(returnItem.createdAt) : 'Дата неизвестна'}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                                                        >
                                                            {returnItem.comment || 'Комментарий не указан'}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                    </Alert>
                                )}

                                <Stack spacing={1.25}>
                                    <Tooltip title={moderationActionHint} disableHoverListener={!moderationActionHint}>
                                        <span>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="success"
                                                startIcon={<CheckCircleIcon />}
                                                onClick={handleSkipExpertApproveClick}
                                                disabled={moderationActionDisabled}
                                            >
                                                Одобрить без проверки
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={sendToExpertsActionHint} disableHoverListener={!sendToExpertsActionHint}>
                                        <span>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="success"
                                                startIcon={<CheckCircleIcon />}
                                                onClick={handleApproveClick}
                                                disabled={sendToExpertsActionDisabled}
                                            >
                                                Отправить экспертам
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={moderationActionHint} disableHoverListener={!moderationActionHint}>
                                        <span>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="error"
                                                startIcon={<CancelIcon />}
                                                onClick={handleRejectClick}
                                                disabled={moderationActionDisabled}
                                            >
                                                Отказать
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={moderationActionHint} disableHoverListener={!moderationActionHint}>
                                        <span>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="warning"
                                                startIcon={<InfoIcon />}
                                                onClick={handleReworkClick}
                                                disabled={moderationActionDisabled}
                                            >
                                                На доработку
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h6">Информация о пользователе</Typography>
                                <Chip
                                    label={userIsInactive ? 'Удален или заблокирован' : 'Активен'}
                                    color={userIsInactive ? 'error' : 'success'}
                                    variant={userIsInactive ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                {adDetail.userInfo.avatarUrl ? (
                                    <Box
                                        component="img"
                                        src={adDetail.userInfo.avatarUrl}
                                        alt="Аватар пользователя"
                                        sx={{
                                            borderRadius: '50%',
                                            width: 64,
                                            height: 64,
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: avatarFallbackBg,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography variant="h6" color="text.secondary">
                                            {adDetail.userInfo.name.charAt(0).toUpperCase()}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {adDetail.userInfo.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 1.5
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                ID пользователя
                                            </Typography>
                                            <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>
                                                {adDetail.userInfo.id ? (
                                                    canOpenUserShow ? (
                                                        <MuiLink
                                                            href={getPlatformUserShowUrl(adDetail.userInfo.id)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="hover"
                                                            sx={{ fontSize: 'inherit' }}
                                                        >
                                                            {adDetail.userInfo.id}
                                                        </MuiLink>
                                                    ) : adDetail.userInfo.id
                                                ) : '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Рейтинг
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: userRatingColor, fontWeight: 700 }}>
                                                {adDetail.userInfo.rating.toFixed(1)} ⭐
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Отзывов
                                            </Typography>
                                            <Typography variant="body1">{adDetail.userInfo.reviewsCount}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Активных объявлений
                                            </Typography>
                                            <Typography variant="body1">{adDetail.userInfo.countActiveAds}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Контекст объявления
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                    gap: 1.5
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Дата создания
                                    </Typography>
                                    <Typography variant="body1">{formatDate(adDetail.creationDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Последнее обновление
                                    </Typography>
                                    <Typography variant="body1">{formatDate(adDetail.lastUpdateDate)}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {adDetail.statusHistory && adDetail.statusHistory.length > 0 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    История статусов
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                    {adDetail.statusHistory.map((status, index) => (
                                        <Chip key={`${status}-${index}`} label={resolveStatusTitle(status)} variant="outlined" />
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            </Box>

            {!adDetail.aiModeration && (
            <Box
                sx={{
                    position: 'fixed',
                    right: { xs: 12, md: 24 },
                    bottom: lockedByAnotherUser ? { xs: 116, md: 140 } : { xs: 12, md: 24 },
                    width: aiWidgetCollapsed ? { xs: 'auto', sm: 260 } : { xs: 'calc(100vw - 24px)', sm: 520 },
                    maxWidth: 'calc(100vw - 24px)',
                    zIndex: (theme) => theme.zIndex.snackbar - 1,
                }}
            >
                <Card
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: aiWidgetBorder,
                        background: aiWidgetBackground,
                        boxShadow: 12,
                        backdropFilter: 'blur(18px)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(120deg, transparent 0%, ${alpha(theme.palette.common.white, isDarkMode ? 0.1 : 0.18)} 45%, transparent 100%)`,
                            transform: 'translateX(-120%)',
                            animation: 'aiWidgetGlow 5.5s linear infinite',
                            pointerEvents: 'none'
                        },
                        '@keyframes aiWidgetGlow': {
                            '0%': {
                                transform: 'translateX(-120%)'
                            },
                            '100%': {
                                transform: 'translateX(120%)'
                            }
                        }
                    }}
                >
                    <Box
                        onClick={() => setAiWidgetCollapsed((prev) => !prev)}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(theme.palette.info.main, isDarkMode ? 0.22 : 0.16),
                                    color: 'info.main',
                                    flexShrink: 0
                                }}
                            >
                                <AutoAwesomeIcon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Rocket AI Summarizer
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {aiLoading
                                        ? `Формирую ответ.. ${formatCountdown(aiSecondsLeft)}`
                                        : aiResponse
                                            ? 'Ответ готов'
                                            : aiError
                                                ? 'Ошибка получения ответа'
                                                : 'Ожидаю ответ от AI'}
                                </Typography>
                            </Box>
                        </Box>
                        <ExpandMoreIcon
                            sx={{
                                transform: aiWidgetCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }}
                        />
                    </Box>

                    {!aiWidgetCollapsed && (
                        <Box sx={{ position: 'relative', zIndex: 1, px: 2, pb: 2, maxHeight: '60vh', overflowY: 'auto' }}>
                            {lockedByAnotherUser && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Саммаризатор недоступен, пока объявление проверяется другим пользователем.
                                </Alert>
                            )}
                            {aiLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" color="text.secondary">
                                        Формирую ответ.. {formatCountdown(aiSecondsLeft)}
                                    </Typography>
                                </Box>
                            )}
                            {!aiLoading && aiError && <Alert severity="error">{aiError}</Alert>}
                            {!aiLoading && aiTimedOut && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Таймаут ответа, перезагрузите страницу
                                    </Typography>
                                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                disabled={lockedByAnotherUser}
                                                onClick={() => {
                                                    if (adDetail) {
                                                        fetchAiResponse(adDetail);
                                                    }
                                                }}
                                            >
                                                Запросить еще раз
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                            {!aiLoading && !aiError && aiResponse && (
                                <Box sx={{ '& pre': { whiteSpace: 'pre-wrap' } }}>
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkBreaks]}
                                        components={{
                                            a: ({ node, ...props }) => (
                                                <a {...props} target="_blank" rel="noreferrer" />
                                            )
                                        }}
                                    >
                                        {aiResponse}
                                    </ReactMarkdown>
                                </Box>
                            )}
                            {!aiLoading && !aiError && !aiResponse && !aiTimedOut && (
                                <Typography variant="body2" color="text.secondary">
                                    Ответ не получен
                                </Typography>
                            )}
                        </Box>
                    )}
                </Card>
            </Box>
            )}

            {/* Модальное окно для просмотра фотографий */}
            <Modal
                open={photoModalOpen}
                onClose={handleCloseModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 24,
                        outline: 'none',
                        overflow: 'hidden'
                    }}
                >
                    {/* Кнопка закрытия */}
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Кнопки зума */}
                    <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1, zIndex: 1 }}>
                        <IconButton
                            onClick={handleZoomIn}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <ZoomInIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleZoomOut}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <ZoomOutIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleResetZoom}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <FullscreenIcon />
                        </IconButton>
                    </Box>

                    {/* Фотография */}
                    {adDetail && adDetail.info.attachmentPhotoIds && (
                        <Box
                            sx={{
                                width: '100%',
                                height: '95vh',
                                overflow: 'hidden',
                                position: 'relative',
                                cursor: isZoomed ? 'grab' : 'default',
                                '&:active': {
                                    cursor: isZoomed ? 'grabbing' : 'default'
                                }
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onWheel={handleWheel}
                        >
                            <Box
                                component="img"
                                src={adDetail.info.attachmentPhotoIds[currentPhotoIndex]}
                                alt={`Фото товара ${currentPhotoIndex + 1}`}
                                sx={{
                                    width: `${zoomLevel * 100}%`,
                                    height: `${zoomLevel * 100}%`,
                                    objectFit: 'contain',
                                    display: 'block',
                                    transition: isZoomed ? 'none' : 'width 0.3s, height 0.3s',
                                    transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                                    userSelect: 'none',
                                    pointerEvents: 'none'
                                }}
                            />
                        </Box>
                    )}

                    {/* Кнопка "Предыдущая" */}
                    {adDetail && adDetail.info.attachmentPhotoIds && adDetail.info.attachmentPhotoIds.length > 1 && (
                        <IconButton
                            onClick={handlePreviousPhoto}
                            sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                zIndex: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                    )}

                    {/* Кнопка "Следующая" */}
                    {adDetail && adDetail.info.attachmentPhotoIds && adDetail.info.attachmentPhotoIds.length > 1 && (
                        <IconButton
                            onClick={handleNextPhoto}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                zIndex: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    )}

                    {/* Индикатор текущей фотографии */}
                    {adDetail && adDetail.info.attachmentPhotoIds && adDetail.info.attachmentPhotoIds.length > 1 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: 1,
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: 1,
                                p: 1
                            }}
                        >
                            {adDetail.info.attachmentPhotoIds.map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: index === currentPhotoIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setCurrentPhotoIndex(index)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Диалоговое окно подтверждения одобрения */}
            <Dialog
                open={approveDialogOpen}
                onClose={handleApproveCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Подтверждение
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Вы уверены, что объявление корректно создано и может быть отправлено далее на экспертную проверку?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleApproveCancel}
                        disabled={approveLoading}
                    >
                        Нет
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button 
                                onClick={handleApproveConfirm}
                                variant="contained"
                                color="success"
                                disabled={approveLoading || isActionLocked}
                                startIcon={approveLoading ? <Skeleton variant="circular" width={20} height={20} /> : <CheckCircleIcon />}
                            >
                                {approveLoading ? 'Одобрение...' : 'Да'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <Dialog
                open={skipExpertDialogOpen}
                onClose={handleSkipExpertCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Одобрить без проверки
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Вы уверены, что объявление не подлежит обязательной проверке и его можно опубликовать без экспертной модерации сразу на платформу?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleSkipExpertCancel}
                        disabled={skipExpertLoading}
                    >
                        Отмена
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button
                                onClick={handleSkipExpertConfirm}
                                variant="contained"
                                color="success"
                                disabled={skipExpertLoading || isActionLocked}
                                startIcon={skipExpertLoading ? <Skeleton variant="circular" width={20} height={20} /> : <CheckCircleIcon />}
                            >
                                {skipExpertLoading ? 'Публикация...' : 'Да, не подлежит, опубликовать'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            {/* Диалоговое окно отказа */}
            <Dialog
                open={rejectDialogOpen}
                onClose={handleRejectCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Отказать в публикации
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Выберите причину отказа:
                    </Typography>
                    
                    <RadioGroup
                        value={selectedRejectReason}
                        onChange={(e) => setSelectedRejectReason(e.target.value)}
                        sx={{ mb: 3 }}
                    >
                        {reasonsLoading ? (
                            <Box sx={{ py: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : rejectReasons.length === 0 ? (
                            <Alert severity="info">Нет причин отказа.</Alert>
                        ) : (
                            rejectReasons.map((reason) => (
                                <FormControlLabel
                                    key={`${reason.label}-${reason.value}`}
                                    value={reason.value}
                                    control={<Radio />}
                                    label={(
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {reason.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                {reason.value}
                                            </Typography>
                                        </Box>
                                    )}
                                    sx={{ mb: 1, alignItems: 'flex-start' }}
                                />
                            ))
                        )}
                    </RadioGroup>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Дополнительный комментарий (опционально):
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Введите дополнительный комментарий..."
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                    />
                    
                    <Alert 
                        severity="info" 
                        icon={<InfoIcon />}
                        sx={{ mt: 2 }}
                    >
                        <Typography variant="body2">
                            Дополнительный комментарий не будет виден пользователю и используется только для внутренней аналитики
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleRejectCancel}
                        disabled={rejectLoading}
                    >
                        Отмена
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button 
                                onClick={handleRejectConfirm}
                                variant="contained"
                                color="error"
                                disabled={rejectLoading || !selectedRejectReason || isActionLocked}
                                startIcon={rejectLoading ? <Skeleton variant="circular" width={20} height={20} /> : <CancelIcon />}
                            >
                                {rejectLoading ? 'Отклонение...' : 'Отказать'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            {/* Диалоговое окно отправки на доработку */}
            <Dialog
                open={reworkDialogOpen}
                onClose={handleReworkCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Отправить на доработку
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Выберите причину для доработки:
                    </Typography>
                    
                    <RadioGroup
                        value={selectedReworkReason}
                        onChange={(e) => setSelectedReworkReason(e.target.value)}
                        sx={{ mb: 3 }}
                    >
                        {reasonsLoading ? (
                            <Box sx={{ py: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : reworkReasons.length === 0 ? (
                            <Alert severity="info">Нет причин доработки.</Alert>
                        ) : (
                            reworkReasons.map((reason) => (
                                <FormControlLabel
                                    key={`${reason.label}-${reason.value}`}
                                    value={reason.value}
                                    control={<Radio />}
                                    label={(
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {reason.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                {reason.value}
                                            </Typography>
                                        </Box>
                                    )}
                                    sx={{ mb: 1, alignItems: 'flex-start' }}
                                />
                            ))
                        )}
                    </RadioGroup>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Дополнительный комментарий (опционально):
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Введите дополнительный комментарий..."
                        value={reworkComment}
                        onChange={(e) => setReworkComment(e.target.value)}
                    />
                    
                    <Alert 
                        severity="info" 
                        icon={<InfoIcon />}
                        sx={{ mt: 2 }}
                    >
                        <Typography variant="body2">
                            Дополнительный комментарий не будет виден пользователю и используется только для внутренней аналитики
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleReworkCancel}
                        disabled={reworkLoading}
                    >
                        Отмена
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button 
                                onClick={handleReworkConfirm}
                                variant="contained"
                                color="warning"
                                disabled={reworkLoading || !selectedReworkReason || isActionLocked}
                                startIcon={reworkLoading ? <Skeleton variant="circular" width={20} height={20} /> : <InfoIcon />}
                            >
                                {reworkLoading ? 'Отправка...' : 'Отправить на доработку'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 
